import React, { useState } from "react";
import Navbar from "../Components/Header/Navbar";
import Footer from "../Components/Footer/Footer";
import minimap from "../img/minimap.png";
import heatmap from "../img/heatmap.png";
import detection from "../img/detection.png";
import clips from "../img/clips.jpg";
import VideoSlider from "../Components/Football/VideoSlider";
import { FaArrowRight, FaArrowCircleDown } from "react-icons/fa";

import tier1 from "../img/tier1-removebg-preview.png";
import tier3 from "../img/tier2-removebg-preview.png";
import tier4 from "../img/tier3-removebg-preview.png";
import tier5 from "../img/tier4-removebg-preview.png";
import tier2 from "../img/tier5-removebg-preview.png";

import { useTranslation } from "react-i18next";
import { FootballTitle } from "src/Components/Football/FootballTitle";

export const Football = () => {
  const [currentImage, setCurrentImage] = useState<ImageKey>("minimap");
  // Estado para acordeón FAQ
  const [faqOpen, setFaqOpen] = useState([false, false, false]);
  const toggleFaq = (idx: number) =>
    setFaqOpen((open) => open.map((v, i) => (i === idx ? !v : v)));
  const { t } = useTranslation();
  const handleImageChange = (imageKey: ImageKey) => {
    setCurrentImage(imageKey);
  };
  type ImageKey = keyof typeof imageDetails;
  const imageDetails = {
    minimap: {
      title: t("football.features.minimap.title"),
      description: t("football.features.minimap.description"),
    },
    heatmap: {
      title: t("football.features.heatmap.title"),
      description: t("football.features.heatmap.description"),
    },
    detection: {
      title: t("football.features.detection.title"),
      description: t("football.features.detection.description"),
    },
  };

  // Tier data with images
  const tiers = [
    {
      img: tier1,
      key: 1,
      color: "from-slate-950 via-indigo-950 to-blue-950",
      shadow: "shadow-yellow-300/20",
    },
    {
      img: tier2,
      key: 2,
      color: "from-slate-950 via-indigo-950 to-blue-950",
      shadow: "shadow-gray-300/20",
    },
    {
      img: tier3,
      key: 3,
      color: "from-slate-950 via-indigo-950 to-blue-950",
      shadow: "shadow-orange-300/20",
    },
    {
      img: tier4,
      key: 4,
      color: "from-slate-950 via-indigo-950 to-blue-950",
      shadow: "shadow-blue-300/20",
    },
    {
      img: tier5,
      key: 5,
      color: "from-slate-950 via-indigo-950 to-blue-950",
      shadow: "shadow-purple-300/20",
    },
  ];

  const eloLevels = [
    {
      rank: 1,
      label: t("football.elo.levels.1.label"),
      percentage: t("football.elo.levels.1.percentage"),
      description: t("football.elo.levels.1.description"),
    },
    {
      rank: 2,
      label: t("football.elo.levels.2.label"),
      percentage: t("football.elo.levels.2.percentage"),
      description: t("football.elo.levels.2.description"),
    },
    {
      rank: 3,
      label: t("football.elo.levels.3.label"),
      percentage: t("football.elo.levels.3.percentage"),
      description: t("football.elo.levels.3.description"),
    },
    {
      rank: 4,
      label: t("football.elo.levels.4.label"),
      percentage: t("football.elo.levels.4.percentage"),
      description: t("football.elo.levels.4.description"),
    },
    {
      rank: 5,
      label: t("football.elo.levels.5.label"),
      percentage: t("football.elo.levels.5.percentage"),
      description: t("football.elo.levels.5.description"),
    },
  ];

  return (
    <div className="bg-gray-50">
      <Navbar />
      <FootballTitle />

      {/* Highlights Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-3xl overflow-hidden shadow-xl transition-transform hover:scale-[1.01]">
              <img
                src={clips}
                className="w-full h-auto object-cover max-h-[500px]"
                alt="Clips preview"
              />
              {/* <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 transition-all">
                  {t("football.highlights.button")}
                  <FaArrowRight />
                </button>
              </div> */}
            </div>

            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t("football.highlights.title")}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t("football.highlights.description")}
              </p>
              <div className="flex flex-wrap gap-4">
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                  {t("football.highlights.features.4k")}
                </span>
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                  {t("football.highlights.features.cloud")}
                </span>
                <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium">
                  {t("football.highlights.features.smartSearch")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ELO Ranking Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-50 via-white to-purple-50">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 uppercase mb-4">
            {t("football.elo.title")}
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            {t("football.elo.description")}
          </p>
          <div className="mb-10 rounded-[2rem] border border-slate-200 bg-white/90 p-6 shadow-xl shadow-black/5 backdrop-blur-xl">
            <div className="mb-5 text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-700">
                {t("football.elo.levelsTitle")}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-slate-900">
                {t("football.elo.levelsSubtitle")}
              </h3>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {t("football.elo.levelsDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {eloLevels.map((level) => (
                <div
                  key={level.rank}
                  className="rounded-2xl border border-slate-200 bg-slate-950 px-4 py-5 text-left shadow-lg shadow-black/10"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold uppercase tracking-[0.3em] text-blue-200/80">
                      {t("football.elo.level")}
                    </span>
                    <span className="text-2xl font-black text-white">
                      {level.rank}
                    </span>
                  </div>
                  <p className="mt-4 text-lg font-semibold text-white">
                    {level.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-blue-200">
                    {level.percentage}
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-300">
                    {level.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          {/* FAQ Card con acordeón colapsable */}
          <div
            className="relative flex flex-col items-center bg-gradient-to-br from-white to-slate-50 rounded-3xl px-8 py-8 shadow-2xl border-4 border-white/70 group overflow-hidden"
            style={{ boxShadow: "0 8px 40px 0 rgba(0,0,0,0.10)" }}
          >
            {/* Glow/shine effect */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br from-white/40 to-transparent rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-all duration-500"></div>
            <span className="text-2xl font-extrabold text-slate-900 drop-shadow-sm mb-2 uppercase tracking-wide text-center">
              {t("football.elo.questions.title")}
            </span>
            <div className="w-full mt-2">
              {/* Pregunta 1 */}
              <div className="mb-2 border-b border-blue-200 last:border-b-0">
                <button
                  className="w-full flex justify-between items-center py-2 focus:outline-none text-left"
                  onClick={() => toggleFaq(0)}
                >
                  <span className="font-semibold text-blue-900">
                    {t("football.elo.questions.first")}
                  </span>
                  <svg
                    className={`w-5 h-5 ml-2 transition-transform duration-200 ${faqOpen[0] ? "rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {faqOpen[0] && (
                  <div className="text-gray-800 text-sm pb-2 px-1 animate-fadeIn">
                    {t("football.elo.questions.firstanswer")}
                  </div>
                )}
              </div>
              {/* Pregunta 2 */}
              <div className="mb-2 border-b border-blue-200 last:border-b-0">
                <button
                  className="w-full flex justify-between items-center py-2 focus:outline-none text-left"
                  onClick={() => toggleFaq(1)}
                >
                  <span className="font-semibold text-blue-900">
                    {t("football.elo.questions.second")}
                  </span>
                  <svg
                    className={`w-5 h-5 ml-2 transition-transform duration-200 ${faqOpen[1] ? "rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {faqOpen[1] && (
                  <div className="text-gray-800 text-sm pb-2 px-1 animate-fadeIn">
                    {t("football.elo.questions.secondanswer")}
                  </div>
                )}
              </div>
              {/* Pregunta 3 */}
              <div className="mb-2 border-b border-blue-200 last:border-b-0">
                <button
                  className="w-full flex justify-between items-center py-2 focus:outline-none text-left"
                  onClick={() => toggleFaq(2)}
                >
                  <span className="font-semibold text-blue-900">
                    {t("football.elo.questions.third")}
                  </span>
                  <svg
                    className={`w-5 h-5 ml-2 transition-transform duration-200 ${faqOpen[2] ? "rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {faqOpen[2] && (
                  <div className="text-gray-800 text-sm pb-2 px-1 animate-fadeIn">
                    {t("football.elo.questions.thirdanswer")}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            {tiers.map((tier, idx) => (
              <div
                key={tier.key}
                className={`relative flex flex-col items-center bg-gradient-to-br ${tier.color} rounded-3xl px-6 py-6 w-full max-w-[220px] shadow-2xl ${tier.shadow} border border-white/10 hover:-translate-y-1 transition-transform duration-300 group overflow-hidden`}
                style={{ boxShadow: "0 8px 40px 0 rgba(0,0,0,0.10)" }}
              >
                {/* Glow/shine effect */}
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br from-white/40 to-transparent rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-all duration-500"></div>
                <img
                  src={tier.img}
                  alt={t(`football.elo.levels.${tier.key}.label`)}
                  className="mb-4 rounded-full shadow-lg animate-float w-28 h-28 object-contain bg-gray-900 p-2 mx-auto"
                />
                <span className="text-sm font-semibold uppercase tracking-[0.3em] text-white/70">
                  {t("football.elo.level")}
                </span>
                <span className="mt-1 text-3xl font-extrabold text-white drop-shadow-sm">
                  {tier.key}
                </span>
                <span className="mt-2 text-center text-lg font-semibold text-white">
                  {t(`football.elo.levels.${tier.key}.label`)}
                </span>
                <span className="mt-1 text-center text-sm font-medium text-blue-100/80">
                  {t(`football.elo.levels.${tier.key}.percentage`)}
                </span>
                {/* Animatd border highlight */}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                {/* <span className="text-3xl mr-3">
                  {imageDetails[currentImage].icon}
                </span> */}
                <h2 className="text-3xl font-bold text-gray-900">
                  {imageDetails[currentImage].title}
                </h2>
              </div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {imageDetails[currentImage].description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mt-12">
                {(["minimap", "heatmap", "detection",] as ImageKey[]).map(
                  (key) => (
                    <button
                      key={key}
                      onClick={() => handleImageChange(key)}
                      className={`px-6 py-3 rounded-full font-medium transition-all ${currentImage === key
                          ? "bg-blue-600 text-white shadow-lg"
                          : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300"
                        }`}
                    >
                      {imageDetails[key].title}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white">
              <img
                src={
                  currentImage === "minimap"
                    ? minimap
                    : currentImage === "heatmap"
                      ? heatmap
                      : detection
                }
                className="w-full h-auto object-cover max-h-[500px]"
                alt={imageDetails[currentImage].title}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      {/* <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t("football.comparison.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("football.comparison.description")}
            </p>
          </div>

          <div className="mb-8 flex justify-between items-center">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900">
                {t("football.comparison.before.label")}
              </h3>
              <p className="text-gray-500">
                {" "}
                {t("football.comparison.before.subtext")}
              </p>
            </div>

            <div className="flex items-center gap-2 bg-blue-100 px-6 py-3 rounded-full">
              <FaArrowCircleDown className="text-blue-600" />
              <span className="font-medium text-blue-600">
                {t("football.comparison.sliderLabel")}
              </span>
              <FaArrowCircleDown className="text-blue-600" />
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900">
                {t("football.comparison.after.label")}
              </h3>
              <p className="text-gray-500">
                {t("football.comparison.after.subtext")}
              </p>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden shadow-2xl">
            <VideoSlider
              videoLeft="https://wzd-cloud-standard-dffkd6cgbeg8e7a7.z03.azurefd.net/wz-2449-es-00004/video_demo_1/high.mp4?sp=r&st=2025-09-30T07:35:50Z&se=2026-10-04T15:50:50Z&spr=https&sv=2024-11-04&sr=b&sig=qBmtUtjit0%2BRbLhLRAUZfLFURwwElSlBhLFlRrEJhtI%3D"
              videoRight="https://wzd-cloud-standard-dffkd6cgbeg8e7a7.z03.azurefd.net/wz-2449-es-00004/video_demo_1/high.mp4?st=2025-09-30T13%3A53%3A59Z&se=2027-02-12T13%3A53%3A59Z&sp=r&spr=https&sv=2025-05-05&sr=b&sig=qiG1yGl7PkUJBX30g0CGP6%2B0WlZytf%2BALb6MgbT4JIA%3D"
            />
          </div>
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

